export interface GoogleReviewItem {
  authorName: string
  authorPhotoUrl?: string
  rating: number
  text: string
  relativeTime?: string
}

export interface GoogleReviewsData {
  syncedAt?: string
  placeName?: string
  rating?: number
  totalReviews?: number
  businessUrl?: string
  reviews: GoogleReviewItem[]
  storedCount?: number
  addedCount?: number
  minRating?: number
}

export const EMPTY_GOOGLE_REVIEWS_DATA: GoogleReviewsData = {
  syncedAt: '',
  businessUrl: '',
  reviews: [],
}

export function parseGoogleReviewsData(
  raw?: string | null,
  fallback: GoogleReviewsData = EMPTY_GOOGLE_REVIEWS_DATA,
): GoogleReviewsData {
  if (!raw) return { ...fallback, reviews: [...fallback.reviews] }

  try {
    const parsed = JSON.parse(raw) as Partial<GoogleReviewsData>
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.reviews)) {
      return { ...fallback, reviews: [...fallback.reviews] }
    }

    return {
      syncedAt: String(parsed.syncedAt ?? '').trim() || undefined,
      placeName: parsed.placeName?.trim() || undefined,
      rating: typeof parsed.rating === 'number' ? parsed.rating : undefined,
      totalReviews: typeof parsed.totalReviews === 'number' ? parsed.totalReviews : undefined,
      businessUrl: parsed.businessUrl?.trim() || undefined,
      reviews: parsed.reviews
        .map((review) => ({
          authorName: String(review.authorName ?? '').trim() || 'Client Google',
          authorPhotoUrl: review.authorPhotoUrl?.trim() || undefined,
          rating: Math.min(5, Math.max(1, Number(review.rating) || 5)),
          text: String(review.text ?? '').trim(),
          relativeTime: review.relativeTime?.trim() || undefined,
        }))
        .filter((review) => review.text.length > 0),
      storedCount: typeof parsed.storedCount === 'number' ? parsed.storedCount : undefined,
      addedCount: typeof parsed.addedCount === 'number' ? parsed.addedCount : undefined,
      minRating: typeof parsed.minRating === 'number' ? parsed.minRating : undefined,
    }
  } catch {
    return { ...fallback, reviews: [...fallback.reviews] }
  }
}

export function parseGoogleReviewsMinRating(raw?: string | number | null): number {
  const value = Number.parseInt(String(raw ?? '1'), 10)
  if (!Number.isFinite(value)) return 1
  return Math.min(5, Math.max(1, value))
}

export function filterGoogleReviewsByMinRating(
  reviews: GoogleReviewItem[],
  minRating: number,
): GoogleReviewItem[] {
  return reviews.filter((review) => review.rating >= minRating)
}
